// ═══════════════════════════════════════════════════════════════════════════════
//  CATÁLOGO DE EJERCICIOS — basado en catalog.md (Excel Básico / Intermedio / Aplicado)
//  Cada constante sigue el schema LESSON_CONFIG documentado en ExcelExerciseTemplate.jsx
// ═══════════════════════════════════════════════════════════════════════════════

// ───────────────────────────── NIVEL BÁSICO ─────────────────────────────

// EX2103 — Funciones y fórmulas: Referencias Relativas/Absolutas/Mixtas
export const EJERCICIO_REFERENCIAS = {
  title: "Referencias Absolutas con $",
  subtitle: "Nivel Básico · Funciones y fórmulas",
  badge: "📌",
  description: "Aprende la diferencia entre referencias relativas y absolutas usando el símbolo $ para fijar una celda al copiar una fórmula a otras filas.",
  completionText: "¡Ahora sabes fijar celdas con $ para que no cambien al copiar fórmulas!",
  introTitle: "El símbolo $ que fija celdas",

  cols: ["A","B","C","D"],
  rows: 10,
  colWidths: { A:170, B:110, C:110, D:110 },

  lockedCells: ["A1","B1","A3","B3","C3","D3","A4","A5","A6","A7","B4","B5","B6","B7"],

  introFunctions: [
    ["$B$1",       "Referencia absoluta: no cambia aunque copies la fórmula"],
    ["=B4*$B$1",   "Multiplica B4 por el valor fijo en B1"],
    ["=B4+C4",     "Suma dos celdas normales (referencia relativa, sin $)"],
  ],

  initialData: {
    A1:"TASA DE IVA FIJA", B1:"0.16",
    A3:"Producto", B3:"Precio", C3:"IVA", D3:"Precio Final",
    A4:"Laptop",  B4:"12000",
    A5:"Mouse",   B5:"250",
    A6:"Teclado", B6:"450",
    A7:"Monitor", B7:"3200",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#7c2d12", color:"white", fontWeight:800, fontSize:12 };
    if (id==="B1") return { bg:"#fef3c7", color:"#92400e", fontWeight:800 };
    if (row===3)   return { bg:"#fde68a", color:"#7c2d12", fontWeight:700, fontSize:12 };
    if ((col==="C"||col==="D") && row>=4 && row<=7) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#fff7ed":"#f8fafc", color:v?.startsWith("=")?"#9a3412":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Precio Final por Producto",
    emptyMsg: "Completa las fórmulas en D4:D7 para ver la gráfica",
    bars: [
      { label:"Laptop",  cellId:"D4", color:"#f59e0b" },
      { label:"Mouse",   cellId:"D5", color:"#3b82f6" },
      { label:"Teclado", cellId:"D6", color:"#8b5cf6" },
      { label:"Monitor", cellId:"D7", color:"#ef4444" },
    ],
    format: (v) => `$${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"r1", phase:1, phaseLabel:"Referencia absoluta",
      label:"Calcula el IVA de la Laptop",
      description:"En C4 multiplica el precio por la tasa de IVA usando referencia absoluta: =B4*$B$1",
      hint:"El símbolo $ antes de la fila y columna ($B$1) fija esa celda para que no cambie al copiar la fórmula",
      concept:"Una referencia absoluta ($B$1) siempre apunta a la misma celda, sin importar en qué fila escribas la fórmula.",
      xp:20, check: c => { const v=c["C4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b4*$b$1"; } },
    { id:"r2", phase:1, phaseLabel:"Referencia absoluta",
      label:"Calcula el IVA de los demás productos",
      description:"Completa C5, C6 y C7 con la misma fórmula, cambiando solo la fila del precio ($B$1 se mantiene fijo)",
      hint:"=B5*$B$1, =B6*$B$1, =B7*$B$1 — el $B$1 nunca cambia",
      concept:"Esta es la ventaja de $: puedes copiar la fórmula a muchas filas y la referencia fija siempre apunta al mismo lugar.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=b${r}*$b$1`; };
        return ok("C5",5)&&ok("C6",6)&&ok("C7",7);
      } },
    { id:"r3", phase:2, phaseLabel:"Sumar el resultado",
      label:"Precio final de la Laptop",
      description:"En D4 suma el precio original más el IVA ya calculado: =B4+C4",
      hint:"C4 ya tiene el monto del IVA calculado, solo súmalo al precio original",
      concept:"Dividir el cálculo en pasos (primero el IVA, luego el total) hace tu hoja más clara y fácil de revisar.",
      xp:20, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b4+c4"; } },
    { id:"r4", phase:2, phaseLabel:"Sumar el resultado",
      label:"Precio final de los demás productos",
      description:"Completa D5, D6 y D7 con la misma suma",
      hint:"=B5+C5, =B6+C6, =B7+C7",
      concept:"Con este patrón puedes calcular el precio final de cualquier lista de productos, sin importar cuántos sean.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=b${r}+c${r}`; };
        return ok("D5",5)&&ok("D6",6)&&ok("D7",7);
      } },
  ],
};


// EX2103/EI2302 — Funciones de Texto: CONCATENAR, MAYUSC, IZQUIERDA/DERECHA
export const EJERCICIO_TEXTO = {
  title: "Funciones de Texto",
  subtitle: "Nivel Básico · Funciones y fórmulas",
  badge: "🔤",
  description: "Aprende a combinar y transformar texto con CONCATENAR, MAYUSC e IZQUIERDA para construir códigos y etiquetas a partir de datos existentes.",
  completionText: "¡Ya sabes combinar y transformar texto para generar códigos automáticamente!",
  introTitle: "Excel también entiende texto",

  cols: ["A","B","C","D","E"],
  rows: 10,
  colWidths: { A:120, B:120, C:150, D:110, E:100 },

  lockedCells: ["A1","A3","B3","C3","D3","E3","A4","A5","A6","B4","B5","B6"],

  introFunctions: [
    ['=CONCATENAR(A4," ",B4)', "Une dos textos con lo que escribas en medio"],
    ["=MAYUSC(A4)",            "Convierte el texto a MAYÚSCULAS"],
    ["=IZQUIERDA(A4,3)",       "Toma los primeros N caracteres del texto"],
  ],

  initialData: {
    A1:"DIRECTORIO DE EMPLEADOS",
    A3:"Nombre", B3:"Apellido", C3:"Nombre Completo", D3:"Mayúsculas", E3:"Abrev.",
    A4:"Ana",   B4:"García",
    A5:"Luis",  B5:"Pérez",
    A6:"María", B6:"López",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if (["C","D","E"].includes(col) && row>=4 && row<=6) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"tx1", phase:1, phaseLabel:"Unir texto",
      label:"Nombre completo de Ana",
      description:'En C4 une el nombre y apellido con un espacio en medio: =CONCATENAR(A4," ",B4)',
      hint:'CONCATENAR(texto1,texto2,...) une los textos en el orden que los escribas',
      concept:"CONCATENAR une varios textos en uno solo. Es útil para generar nombres completos, códigos o etiquetas a partir de columnas separadas.",
      xp:20, check: c => { const v=c["C4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=concatenar(a4,"",b4)'; } },
    { id:"tx2", phase:1, phaseLabel:"Unir texto",
      label:"Nombre completo de Luis y María",
      description:"Completa C5 y C6 con la misma fórmula, cambiando la fila",
      hint:'=CONCATENAR(A5," ",B5) y =CONCATENAR(A6," ",B6)',
      concept:"Igual que con fórmulas numéricas, las funciones de texto se replican cambiando solo el número de fila.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=concatenar(a${r},"",b${r})`; };
        return ok("C5",5)&&ok("C6",6);
      } },
    { id:"tx3", phase:2, phaseLabel:"Transformar texto",
      label:"Nombre en mayúsculas",
      description:"En D4 convierte el nombre de Ana a mayúsculas: =MAYUSC(A4)",
      hint:"MAYUSC(texto) convierte cualquier texto a mayúsculas",
      concept:"MAYUSC() es útil para estandarizar datos, por ejemplo nombres o códigos que deben mostrarse siempre en mayúsculas.",
      xp:15, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=mayusc(a4)"||v==="=upper(a4)"; } },
    { id:"tx4", phase:2, phaseLabel:"Transformar texto",
      label:"Primeras letras del apellido",
      description:"En E4 extrae las primeras 3 letras del apellido de Ana: =IZQUIERDA(B4,3)",
      hint:"IZQUIERDA(texto,núm_caracteres) toma los caracteres desde la izquierda",
      concept:"IZQUIERDA() y DERECHA() son ideales para generar abreviaciones o validar el formato de un texto.",
      xp:20, check: c => { const v=c["E4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=izquierda(b4,3)"||v==="=left(b4,3)"; } },
  ],
};


// EX2104 — Listas: Duplicados con CONTAR.SI
export const EJERCICIO_VALIDACION = {
  title: "Detecta Duplicados con CONTAR.SI",
  subtitle: "Nivel Básico · Listas",
  badge: "🔍",
  description: "Usa CONTAR.SI para detectar valores duplicados en una lista antes de validarla, una técnica esencial para mantener datos limpios.",
  completionText: "¡Ahora puedes detectar duplicados automáticamente con CONTAR.SI!",
  introTitle: "Encuentra duplicados sin buscarlos a mano",

  cols: ["A","B","C"],
  rows: 11,
  colWidths: { A:180, B:110, C:110 },

  lockedCells: ["A1","A3","B3","C3","A4","A5","A6","A7","A8","A9"],

  introFunctions: [
    ["=CONTAR.SI(rango,criterio)", "Cuenta cuántas veces aparece un valor en un rango"],
    ['=SI(B4>1,"Duplicado","Único")', "Usa el conteo para etiquetar el estado del registro"],
  ],

  initialData: {
    A1:"LISTA DE CORREOS REGISTRADOS",
    A3:"Correo", B3:"Repeticiones", C3:"Estado",
    A4:"ana@mail.com",
    A5:"luis@mail.com",
    A6:"maria@mail.com",
    A7:"ana@mail.com",
    A8:"carlos@mail.com",
    A9:"luis@mail.com",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if ((col==="B"||col==="C") && row>=4 && row<=9) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"v1", phase:1, phaseLabel:"Contar repeticiones",
      label:"Repeticiones del correo de Ana",
      description:"En B4 cuenta cuántas veces aparece el correo de A4 en toda la lista: =CONTAR.SI(A4:A9,A4)",
      hint:"CONTAR.SI(A4:A9,A4) cuenta cuántas veces se repite el valor de A4 dentro del rango A4:A9",
      concept:"CONTAR.SI(rango, criterio) cuenta cuántas celdas del rango cumplen una condición. Aquí el criterio es 'igual al valor de A4'.",
      xp:20, check: c => { const v=c["B4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=contar.si(a4:a9,a4)"||v==="=countif(a4:a9,a4)"; } },
    { id:"v2", phase:1, phaseLabel:"Contar repeticiones",
      label:"Repeticiones de los demás correos",
      description:"Completa B5 a B9 con la misma lógica, cambiando solo la fila del criterio",
      hint:"=CONTAR.SI(A4:A9,A5), y así sucesivamente — el rango A4:A9 se mantiene igual",
      concept:"El rango de búsqueda (A4:A9) siempre es el mismo; solo cambia la celda que se busca dentro de él.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=contar.si(a4:a9,a${r})`||v===`=countif(a4:a9,a${r})`; };
        return ok("B5",5)&&ok("B6",6)&&ok("B7",7)&&ok("B8",8)&&ok("B9",9);
      } },
    { id:"v3", phase:2, phaseLabel:"Etiquetar con SI",
      label:"Marca el estado del correo de Ana",
      description:'En C4 usa SI para etiquetar el resultado de B4: si es mayor a 1, escribe "Duplicado", si no "Único": =SI(B4>1,"Duplicado","Único")',
      hint:'=SI(B4>1,"Duplicado","Único") — B4 ya tiene el conteo calculado',
      concept:"Dividir un problema en pasos (primero contar con CONTAR.SI, luego decidir con SI) hace tus fórmulas más claras y fáciles de revisar.",
      xp:30, check: c => { const v=c["C4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(b4>1,"duplicado","único")'||v==='=if(b4>1,"duplicado","único")'; } },
    { id:"v4", phase:2, phaseLabel:"Etiquetar con SI",
      label:"Marca el estado de los demás correos",
      description:"Completa C5 a C9 con la misma fórmula, cambiando la fila",
      hint:"=SI(B5>1,...), =SI(B6>1,...) y así sucesivamente",
      concept:"Con esta clasificación automática, Excel te dice de inmediato cuáles registros están duplicados en una lista de cientos de filas.",
      xp:30, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(b${r}>1,"duplicado","único")`||v===`=if(b${r}>1,"duplicado","único")`; };
        return ok("C5",5)&&ok("C6",6)&&ok("C7",7)&&ok("C8",8)&&ok("C9",9);
      } },
  ],
};


// ───────────────────────────── NIVEL INTERMEDIO ─────────────────────────────

// EI2302 — Funciones: BUSCARV | BUSCARV
export const EJERCICIO_BUSCARV = {
  title: "Búsqueda con BUSCARV",
  subtitle: "Nivel Intermedio · Funciones",
  badge: "🔎",
  description: "Aprende a usar BUSCARV para encontrar información relacionada en una tabla, como la categoría o el precio de un producto a partir de su nombre.",
  completionText: "¡Ya sabes usar BUSCARV, una de las funciones más usadas en el mundo laboral!",
  introTitle: "Busca datos automáticamente con BUSCARV",

  cols: ["A","B","C","D"],
  rows: 13,
  colWidths: { A:150, B:130, C:110, D:150 },

  lockedCells: ["A1","A3","B3","C3","A4","A5","A6","A7","B4","B5","B6","B7","C4","C5","C6","C7","A9","A10","A11","A12"],

  introFunctions: [
    ["=BUSCARV(valor,rango_tabla,núm_columna,FALSO)", "Busca un valor en la 1ª columna de un rango y devuelve un dato de otra columna"],
    ["FALSO", "Indica que la búsqueda debe ser exacta (recomendado)"],
  ],

  initialData: {
    A1:"CATÁLOGO DE PRODUCTOS",
    A3:"Producto", B3:"Categoría", C3:"Precio",
    A4:"Laptop",  B4:"Cómputo",    C4:"12000",
    A5:"Mouse",   B5:"Accesorios", C5:"250",
    A6:"Teclado", B6:"Accesorios", C6:"450",
    A7:"Monitor", B7:"Cómputo",    C7:"3200",
    A9:"CONSULTA RÁPIDA",
    A10:"Producto a buscar →", B10:"Teclado",
    A11:"Categoría encontrada →",
    A12:"Precio encontrado →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#1d6f42", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#d1fae5", color:"#064e3b", fontWeight:700, fontSize:12 };
    if (id==="A9") return { bg:"#1d6f42", color:"white", fontWeight:700, fontSize:11 };
    if (id==="B10") return { bg:"#fefce8", color:"#92400e", fontWeight:700 };
    if (id==="B11"||id==="B12") {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f0fdf4":"#f8fafc", color:v?.startsWith("=")?"#166534":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Precios del Catálogo",
    emptyMsg: "El catálogo ya tiene precios cargados",
    bars: [
      { label:"Laptop",  cellId:"C4", color:"#1d6f42" },
      { label:"Mouse",   cellId:"C5", color:"#3b82f6" },
      { label:"Teclado", cellId:"C6", color:"#f59e0b" },
      { label:"Monitor", cellId:"C7", color:"#ef4444" },
    ],
    format: (v) => `$${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"bv1", phase:1, phaseLabel:"Búsqueda básica",
      label:"Encuentra la categoría",
      description:"En B11 busca la categoría del producto en B10 dentro de la tabla A4:C7: =BUSCARV(B10,A4:C7,2,FALSO)",
      hint:"BUSCARV(B10, A4:C7, 2, FALSO): busca B10 en la 1ª columna del rango y devuelve la columna 2 (Categoría)",
      concept:"BUSCARV busca un valor en la PRIMERA columna del rango y regresa un dato de otra columna en la misma fila. El número de columna se cuenta desde la primera columna del rango (1=A, 2=B, 3=C).",
      xp:30, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=buscarv(b10,a4:c7,2,falso)"||v==="=vlookup(b10,a4:c7,2,false)"; } },
    { id:"bv2", phase:1, phaseLabel:"Búsqueda básica",
      label:"Encuentra el precio",
      description:"En B12 busca el precio del mismo producto: =BUSCARV(B10,A4:C7,3,FALSO)",
      hint:"Cambia solo el número de columna a 3, ya que Precio es la 3ª columna del rango",
      concept:"Puedes usar BUSCARV varias veces con la misma búsqueda, cambiando solo la columna que quieres traer.",
      xp:25, check: c => { const v=c["B12"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=buscarv(b10,a4:c7,3,falso)"||v==="=vlookup(b10,a4:c7,3,false)"; } },
    { id:"bv3", phase:2, phaseLabel:"Cambia el criterio",
      label:"Prueba con otro producto",
      description:'Cambia el valor de B10 a "Mouse" y confirma que B11 y B12 se actualizan automáticamente',
      hint:"Solo edita B10 — no toques las fórmulas de B11 ni B12",
      concept:"Esta es la magia de BUSCARV: una sola fórmula sirve para consultar cualquier producto, solo cambiando el valor de búsqueda.",
      xp:20, check: c => c["B10"]?.trim().toLowerCase()==="mouse" },
  ],
};


// EI2302 — Funciones: SUMAR.SI y CONTAR.SI
export const EJERCICIO_SUMARSI = {
  title: "SUMAR.SI y CONTAR.SI",
  subtitle: "Nivel Intermedio · Funciones",
  badge: "➕",
  description: "Suma y cuenta datos que cumplen una condición específica, sin necesidad de filtrar manualmente la información.",
  completionText: "¡Dominaste SUMAR.SI y CONTAR.SI, funciones clave para resumir datos con condiciones!",
  introTitle: "Suma y cuenta con condiciones",

  cols: ["A","B","C","D"],
  rows: 14,
  colWidths: { A:150, B:110, C:110, D:150 },

  lockedCells: ["A1","A3","B3","C3","A4","A5","A6","A7","A8","B4","B5","B6","B7","B8","C4","C5","C6","C7","C8","A10","A11","A12","A13"],

  introFunctions: [
    ["=SUMAR.SI(rango,criterio,rango_suma)", "Suma solo las celdas que cumplen una condición"],
    ["=CONTAR.SI(rango,criterio)", "Cuenta solo las celdas que cumplen una condición"],
    [">1000", "Ejemplo de criterio: mayor a 1000"],
  ],

  initialData: {
    A1:"VENTAS POR VENDEDOR",
    A3:"Vendedor", B3:"Región", C3:"Venta",
    A4:"Roberto", B4:"Norte", C4:"8500",
    A5:"Carmen",  B5:"Sur",   C5:"6200",
    A6:"Miguel",  B6:"Norte", C6:"9500",
    A7:"Daniela", B7:"Sur",   C7:"7200",
    A8:"Pedro",   B8:"Norte", C8:"5300",
    A10:"RESUMEN",
    A11:"Total ventas Región Norte →",
    A12:"Cuántos vendedores en Norte →",
    A13:"Ventas mayores a $7,000 (conteo) →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#1d6f42", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#d1fae5", color:"#064e3b", fontWeight:700, fontSize:12 };
    if (id==="A10") return { bg:"#1d6f42", color:"white", fontWeight:700, fontSize:11 };
    if (["B11","B12","B13"].includes(id)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f0fdf4":"#f8fafc", color:v?.startsWith("=")?"#166534":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Ventas por Vendedor",
    emptyMsg: "Ya hay ventas registradas por vendedor",
    bars: [
      { label:"Roberto", cellId:"C4", color:"#1d6f42" },
      { label:"Carmen",  cellId:"C5", color:"#3b82f6" },
      { label:"Miguel",  cellId:"C6", color:"#f59e0b" },
      { label:"Daniela", cellId:"C7", color:"#ef4444" },
      { label:"Pedro",   cellId:"C8", color:"#8b5cf6" },
    ],
    format: (v) => `$${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"sc1", phase:1, phaseLabel:"Sumar con condición",
      label:"Total de ventas en el Norte",
      description:'En B11 suma solo las ventas de la Región Norte: =SUMAR.SI(B4:B8,"Norte",C4:C8)',
      hint:'SUMAR.SI(rango_condición, criterio, rango_a_sumar) — busca "Norte" en B4:B8 y suma la fila correspondiente de C4:C8',
      concept:"SUMAR.SI suma solo las celdas de un rango que cumplen una condición, sin necesidad de filtrar la tabla manualmente.",
      xp:30, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=sumar.si(b4:b8,"norte",c4:c8)'||v==='=sumif(b4:b8,"norte",c4:c8)'; } },
    { id:"sc2", phase:1, phaseLabel:"Contar con condición",
      label:"Cuántos vendedores hay en el Norte",
      description:'En B12 cuenta cuántos vendedores pertenecen a la Región Norte: =CONTAR.SI(B4:B8,"Norte")',
      hint:'CONTAR.SI(rango,criterio) — cuenta las celdas de B4:B8 que dicen "Norte"',
      concept:"CONTAR.SI es igual que SUMAR.SI pero solo cuenta las coincidencias, no suma ningún valor.",
      xp:25, check: c => { const v=c["B12"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=contar.si(b4:b8,"norte")'||v==='=countif(b4:b8,"norte")'; } },
    { id:"sc3", phase:2, phaseLabel:"Criterios numéricos",
      label:"Cuántas ventas superan los $7,000",
      description:'En B13 cuenta cuántas ventas en C4:C8 son mayores a 7000: =CONTAR.SI(C4:C8,">7000")',
      hint:'El criterio puede ser una comparación como ">7000", siempre entre comillas',
      concept:"Los criterios de CONTAR.SI y SUMAR.SI también pueden ser comparaciones (>, <, >=, <=) y no solo textos exactos.",
      xp:25, check: c => { const v=c["B13"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=contar.si(c4:c8,">7000")'||v==='=countif(c4:c8,">7000")'; } },
  ],
};


// EI1604/EA1610 — Datos: Cálculo de vencimientos (resta de fechas + SI)
export const EJERCICIO_FECHAS = {
  title: "Cálculo de Vencimientos",
  subtitle: "Nivel Intermedio · Datos",
  badge: "📅",
  description: "Calcula días transcurridos entre fechas y clasifica facturas como vencidas o vigentes usando restas y la función SI.",
  completionText: "¡Ahora puedes calcular vencimientos automáticamente, una habilidad clave en cobranza y finanzas!",
  introTitle: "Fechas representadas como número de día",

  cols: ["A","B","C","D","E"],
  rows: 10,
  colWidths: { A:110, B:130, C:110, D:150, E:110 },

  lockedCells: ["A1","A3","B3","C3","D3","E3","A4","A5","A6","A7","B4","B5","B6","B7","C4","C5","C6","C7","A9"],

  introFunctions: [
    ["=C4-B4", "Resta dos números de día para obtener días transcurridos"],
    ['=SI(D4>30,"Vencido","Vigente")', "Clasifica según los días transcurridos"],
  ],

  initialData: {
    A1:"CONTROL DE VENCIMIENTOS (días del año)",
    A3:"Cliente", B3:"Día de emisión", C3:"Día actual", D3:"Días transcurridos", E3:"Estado",
    A4:"Roberto", B4:"45", C4:"120",
    A5:"Carmen",  B5:"90", C5:"120",
    A6:"Miguel",  B6:"10", C6:"120",
    A7:"Daniela", B7:"70", C7:"120",
    A9:'Regla: más de 30 días = "Vencido", si no = "Vigente"',
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:12 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if (id==="A9") return { bg:"#f5f3ff", color:"#5b21b6", fontSize:11, italic:true };
    if ((col==="D"||col==="E") && row>=4 && row<=7) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"fc1", phase:1, phaseLabel:"Restar fechas",
      label:"Días transcurridos de Roberto",
      description:"En D4 resta el día de emisión al día actual: =C4-B4",
      hint:"C4-B4 te da cuántos días han pasado desde la emisión",
      concept:"En Excel, las fechas se guardan como números (días). Por eso restar dos fechas te da directamente el número de días entre ellas.",
      xp:20, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=c4-b4"; } },
    { id:"fc2", phase:1, phaseLabel:"Restar fechas",
      label:"Días transcurridos de los demás clientes",
      description:"Completa D5, D6 y D7 con la misma resta",
      hint:"=C5-B5, =C6-B6, =C7-B7",
      concept:"Replicar la resta en cada fila te permite calcular la antigüedad de muchas facturas a la vez.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=c${r}-b${r}`; };
        return ok("D5",5)&&ok("D6",6)&&ok("D7",7);
      } },
    { id:"fc3", phase:2, phaseLabel:"Clasificar vencimiento",
      label:"Estado de Roberto",
      description:'En E4 usa SI para mostrar "Vencido" si D4>30, si no "Vigente": =SI(D4>30,"Vencido","Vigente")',
      hint:'=SI(D4>30,"Vencido","Vigente") — D4 ya tiene los días calculados',
      concept:"Al separar el cálculo (D4) de la decisión (E4), tu hoja es más fácil de revisar y depurar.",
      xp:25, check: c => { const v=c["E4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(d4>30,"vencido","vigente")'||v==='=if(d4>30,"vencido","vigente")'; } },
    { id:"fc4", phase:2, phaseLabel:"Clasificar vencimiento",
      label:"Estado de los demás clientes",
      description:"Completa E5, E6 y E7 con la misma lógica",
      hint:"Misma fórmula, cambia solo la fila (D5, D6, D7)",
      concept:"Con esta clasificación automática, un equipo de cobranza identifica de inmediato qué facturas requieren seguimiento urgente.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(d${r}>30,"vencido","vigente")`||v===`=if(d${r}>30,"vencido","vigente")`; };
        return ok("E5",5)&&ok("E6",6)&&ok("E7",7);
      } },
  ],
};


// ───────────────────────────── NIVEL APLICADO ─────────────────────────────

// EA1609 — Facturación: BUSCARV aplicado
export const EJERCICIO_FACTURACION = {
  title: "Automatiza una Factura con BUSCARV",
  subtitle: "Nivel Aplicado · Facturación",
  badge: "🧾",
  description: "Construye una mini factura que trae automáticamente el precio de un producto desde un catálogo, usando BUSCARV para evitar capturar datos manualmente.",
  completionText: "¡Construiste una factura que se autocompleta! Así funcionan los sistemas reales de facturación.",
  introTitle: "Una factura que se llena sola",

  cols: ["A","B","C","D"],
  rows: 14,
  colWidths: { A:130, B:130, C:100, D:110 },

  lockedCells: ["A1","A3","B3","A4","A5","A6","A7","B4","B5","B6","B7","A9","A10","B10","C10","D10","A11","C11","A12","C12"],

  introFunctions: [
    ["=BUSCARV(valor,tabla,columna,FALSO)", "Trae el precio del catálogo automáticamente"],
    ["=B11*C11", "Multiplica el precio encontrado por la cantidad solicitada"],
  ],

  initialData: {
    A1:"CATÁLOGO",
    A3:"Producto", B3:"Precio",
    A4:"Laptop",  B4:"12000",
    A5:"Mouse",   B5:"250",
    A6:"Teclado", B6:"450",
    A7:"Monitor", B7:"3200",
    A9:"FACTURA",
    A10:"Producto", B10:"Precio Unitario", C10:"Cantidad", D10:"Subtotal",
    A11:"Teclado", C11:"3",
    A12:"Monitor", C12:"2",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1"||id==="A9") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:12 };
    if (row===10) return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if ((id==="B11"||id==="B12"||id==="D11"||id==="D12")) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"fa1", phase:1, phaseLabel:"Traer el precio",
      label:"Precio unitario del Teclado",
      description:"En B11 busca el precio del producto de A11 en el catálogo A4:B7: =BUSCARV(A11,A4:B7,2,FALSO)",
      hint:"BUSCARV(A11,A4:B7,2,FALSO) busca 'Teclado' en la columna A del catálogo y regresa la columna 2 (Precio)",
      concept:"En una factura real, el precio nunca se escribe a mano: se trae del catálogo con BUSCARV para evitar errores de captura.",
      xp:30, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=buscarv(a11,a4:b7,2,falso)"||v==="=vlookup(a11,a4:b7,2,false)"; } },
    { id:"fa2", phase:1, phaseLabel:"Traer el precio",
      label:"Precio unitario del Monitor",
      description:"En B12 haz lo mismo para el producto de A12",
      hint:"=BUSCARV(A12,A4:B7,2,FALSO)",
      concept:"La misma fórmula sirve para cualquier producto del catálogo, solo cambia la fila de consulta.",
      xp:25, check: c => { const v=c["B12"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=buscarv(a12,a4:b7,2,falso)"||v==="=vlookup(a12,a4:b7,2,false)"; } },
    { id:"fa3", phase:2, phaseLabel:"Calcular subtotal",
      label:"Subtotal del Teclado",
      description:"En D11 multiplica el precio unitario por la cantidad: =B11*C11",
      hint:"B11 ya tiene el precio (traído con BUSCARV), solo multiplícalo por la cantidad en C11",
      concept:"Separar 'buscar el precio' (BUSCARV) de 'calcular el subtotal' (multiplicación) hace que la factura sea más fácil de auditar.",
      xp:20, check: c => { const v=c["D11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b11*c11"; } },
    { id:"fa4", phase:2, phaseLabel:"Calcular subtotal",
      label:"Subtotal del Monitor",
      description:"En D12 calcula el subtotal: =B12*C12",
      hint:"=B12*C12",
      concept:"Con este patrón (BUSCARV + multiplicación) puedes armar una factura completa para decenas de productos.",
      xp:20, check: c => { const v=c["D12"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b12*c12"; } },
  ],
};


// EA1613/EI2302 — Excel Experto: SI.ERROR con BUSCARV
export const EJERCICIO_SIERROR = {
  title: "Manejo de Errores con SI.ERROR",
  subtitle: "Nivel Aplicado · Excel Experto",
  badge: "🛡️",
  description: "Evita que tus fórmulas muestren errores como #N/A cuando una búsqueda no encuentra resultado, usando SI.ERROR para mostrar un mensaje amigable.",
  completionText: "¡Ahora tus fórmulas nunca mostrarán errores feos a otros usuarios!",
  introTitle: "Cuando BUSCARV no encuentra nada",

  cols: ["A","B","C"],
  rows: 11,
  colWidths: { A:150, B:130, C:110 },

  lockedCells: ["A1","A3","B3","A4","A5","A6","B4","B5","B6","A9","A10","A11"],

  introFunctions: [
    ["=SI.ERROR(fórmula,valor_si_error)", "Si la fórmula da error, muestra el segundo valor en su lugar"],
    ['=SI.ERROR(BUSCARV(B10,A4:B6,2,FALSO),"No encontrado")', "Evita el error cuando el producto no existe en el catálogo"],
  ],

  initialData: {
    A1:"CATÁLOGO",
    A3:"Producto", B3:"Precio",
    A4:"Laptop",  B4:"12000",
    A5:"Mouse",   B5:"250",
    A6:"Teclado", B6:"450",
    A9:"CONSULTA",
    A10:"Producto a buscar →", B10:"Bocina",
    A11:"Precio encontrado →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1"||id==="A9") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:12 };
    if (row===3) return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if (id==="B10") return { bg:"#fefce8", color:"#92400e", fontWeight:700 };
    if (id==="B11") {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"se1", phase:1, phaseLabel:"El problema del error",
      label:"Busca un producto que no existe",
      description:'En B11 intenta buscar el precio de "Bocina": =BUSCARV(B10,A4:B6,2,FALSO). Verás un error porque "Bocina" no está en el catálogo.',
      hint:"Escribe la fórmula tal cual, el error es esperado — lo resolverás en el siguiente objetivo",
      concept:"BUSCARV muestra un error cuando no encuentra el valor buscado. Mostrar este error a un usuario final se ve poco profesional.",
      xp:15, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=buscarv(b10,a4:b6,2,falso)"||v==="=vlookup(b10,a4:b6,2,false)"; } },
    { id:"se2", phase:2, phaseLabel:"Resolver con SI.ERROR",
      label:"Envuelve la fórmula con SI.ERROR",
      description:'Modifica B11 para envolver la búsqueda con SI.ERROR: =SI.ERROR(BUSCARV(B10,A4:B6,2,FALSO),"No encontrado")',
      hint:'SI.ERROR(fórmula, "texto alternativo") — si la fórmula falla, muestra el texto en vez del error',
      concept:"SI.ERROR es la forma profesional de manejar errores: en vez de un error feo, el usuario ve un mensaje claro y entendible.",
      xp:35, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si.error(buscarv(b10,a4:b6,2,falso),"noencontrado")'||v==='=iferror(vlookup(b10,a4:b6,2,false),"noencontrado")'; } },
    { id:"se3", phase:2, phaseLabel:"Resolver con SI.ERROR",
      label:"Prueba con un producto que sí existe",
      description:'Cambia B10 a "Mouse" y confirma que ahora sí aparece el precio en B11',
      hint:"Solo cambia el valor de B10, no la fórmula de B11",
      concept:"SI.ERROR no oculta datos correctos: solo reemplaza el resultado cuando realmente hay un error.",
      xp:20, check: c => c["B10"]?.trim().toLowerCase()==="mouse" },
  ],
};


// EA1610 — Cuentas por Cobrar: SI + Y con múltiples condiciones
export const EJERCICIO_CUENTASCOBRAR = {
  title: "Cuentas por Cobrar: Múltiples Condiciones",
  subtitle: "Nivel Aplicado · Cuentas por Cobrar",
  badge: "💰",
  description: "Clasifica facturas en Vigente, Por vencer o Vencida combinando SI con Y para evaluar varias condiciones de días de vencimiento a la vez.",
  completionText: "¡Puedes construir reglas de cobranza con múltiples condiciones, tal como en un sistema de CRM real!",
  introTitle: "Cuando una condición no es suficiente",

  cols: ["A","B","C"],
  rows: 10,
  colWidths: { A:110, B:150, C:130 },

  lockedCells: ["A1","A3","B3","C3","A4","A5","A6","A7","B4","B5","B6","B7","A9"],

  introFunctions: [
    ["=Y(condición1,condición2)", "Verdadero solo si AMBAS condiciones se cumplen"],
    ['=SI(Y(B4>10,B4<=30),"Por vencer","Otro")', "Combina Y() con SI() para evaluar un rango de días"],
  ],

  initialData: {
    A1:"CUENTAS POR COBRAR",
    A3:"Cliente", B3:"Días de vencimiento", C3:"Estado",
    A4:"Roberto", B4:"5",
    A5:"Carmen",  B5:"20",
    A6:"Miguel",  B6:"45",
    A7:"Daniela", B7:"15",
    A9:"Regla: 0-10 Vigente | 11-30 Por vencer | 31+ Vencida",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:12 };
    if (row===3)   return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if (id==="A9") return { bg:"#f5f3ff", color:"#5b21b6", fontSize:11, italic:true };
    if (id[0]==="C" && row>=4 && row<=7) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"cc1", phase:1, phaseLabel:"Condición doble con Y()",
      label:"¿Carmen está \"Por vencer\"?",
      description:'En C5 usa Y() dentro de SI para saber si Carmen está "Por vencer" (entre 11 y 30 días): =SI(Y(B5>10,B5<=30),"Por vencer","Revisar")',
      hint:'Y(B5>10,B5<=30) es verdadero solo si B5 es MAYOR a 10 Y MENOR O IGUAL a 30 al mismo tiempo',
      concept:"Y() te permite exigir que dos condiciones se cumplan simultáneamente. Sin Y(), tendrías que anidar dos SI por separado.",
      xp:30, check: c => { const v=c["C5"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(y(b5>10,b5<=30),"porvencer","revisar")'||v==='=if(and(b5>10,b5<=30),"porvencer","revisar")'; } },
    { id:"cc2", phase:1, phaseLabel:"Condición doble con Y()",
      label:"Repite para los demás clientes",
      description:"Completa C4, C6 y C7 con la misma fórmula, cambiando la fila",
      hint:"=SI(Y(B4>10,B4<=30),...), y así sucesivamente",
      concept:"Con esta fórmula ya identificas quién está 'Por vencer' entre docenas de clientes, sin revisar uno por uno.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(y(b${r}>10,b${r}<=30),"porvencer","revisar")`||v===`=if(and(b${r}>10,b${r}<=30),"porvencer","revisar")`; };
        return ok("C4",4)&&ok("C6",6)&&ok("C7",7);
      } },
    { id:"cc3", phase:2, phaseLabel:"Tercera categoría",
      label:"Clasifica correctamente a Roberto (Vigente/Por vencer/Vencida)",
      description:'Ahora reemplaza la fórmula de C4 por una versión completa con SI anidado: =SI(B4<=10,"Vigente",SI(B4<=30,"Por vencer","Vencida"))',
      hint:'Si B4<=10 es Vigente; si no, revisa si B4<=30 para Por vencer; si tampoco, es Vencida',
      concept:"Cuando hay más de dos posibles resultados, se combina SI anidado (para las categorías) con comparaciones simples, sin necesitar Y() en este caso.",
      xp:35, check: c => { const v=c["C4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(b4<=10,"vigente",si(b4<=30,"porvencer","vencida"))'||v==='=if(b4<=10,"vigente",if(b4<=30,"porvencer","vencida"))'; } },
    { id:"cc4", phase:2, phaseLabel:"Tercera categoría",
      label:"Aplica la clasificación completa a todos",
      description:"Completa C5, C6 y C7 con el mismo SI anidado",
      hint:"Misma fórmula, cambia solo el número de fila en cada B",
      concept:"Esta es la fórmula que un sistema de cobranza real usaría para clasificar automáticamente cientos de cuentas por cobrar.",
      xp:35, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(b${r}<=10,"vigente",si(b${r}<=30,"porvencer","vencida"))`||v===`=if(b${r}<=10,"vigente",if(b${r}<=30,"porvencer","vencida"))`; };
        return ok("C5",5)&&ok("C6",6)&&ok("C7",7);
      } },
  ],
};


// ───────────────────────────── NIVEL BÁSICO (continuación) ─────────────────────────────

// EX2103 — Funciones Matemáticas: MAX, MIN, PROMEDIO, CONTAR
export const EJERCICIO_ESTADISTICAS = {
  title: "Funciones Estadísticas Básicas",
  subtitle: "Nivel Básico · Funciones y fórmulas",
  badge: "📈",
  description: "Aprende a resumir un conjunto de datos con MAX, MIN, PROMEDIO y CONTAR — las bases de cualquier análisis estadístico en Excel.",
  completionText: "¡Ya sabes resumir datos con las 4 funciones estadísticas más usadas en Excel!",
  introTitle: "Resume datos de un vistazo",

  cols: ["A","B"],
  rows: 13,
  colWidths: { A:170, B:120 },

  lockedCells: ["A1","A3","B3","A4","A5","A6","A7","A8","B4","B5","B6","B7","B8","A10","A11","A12","A13"],

  introFunctions: [
    ["=MAX(rango)", "Devuelve el valor más alto del rango"],
    ["=MIN(rango)", "Devuelve el valor más bajo del rango"],
    ["=PROMEDIO(rango)", "Calcula el promedio (media) del rango"],
    ["=CONTAR(rango)", "Cuenta cuántas celdas del rango tienen un número"],
  ],

  initialData: {
    A1:"CALIFICACIONES DEL GRUPO",
    A3:"Alumno", B3:"Calificación",
    A4:"Ana",  B4:"92",
    A5:"Luis", B5:"78",
    A6:"María",B6:"85",
    A7:"Juan", B7:"64",
    A8:"Sofía",B8:"97",
    A10:"Calificación más alta →",
    A11:"Calificación más baja →",
    A12:"Promedio del grupo →",
    A13:"Alumnos evaluados →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if (["B10","B11","B12","B13"].includes(id)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Calificaciones por Alumno",
    emptyMsg: "Ya hay calificaciones registradas",
    bars: [
      { label:"Ana",   cellId:"B4", color:"#3b82f6" },
      { label:"Luis",  cellId:"B5", color:"#f59e0b" },
      { label:"María", cellId:"B6", color:"#8b5cf6" },
      { label:"Juan",  cellId:"B7", color:"#ef4444" },
      { label:"Sofía", cellId:"B8", color:"#22c55e" },
    ],
    format: (v) => `${v} pts`,
  },

  objectives: [
    { id:"e1", phase:1, phaseLabel:"Máximo y mínimo",
      label:"Calificación más alta",
      description:"En B10 encuentra la calificación más alta del grupo: =MAX(B4:B8)",
      hint:"MAX(rango) devuelve el valor más grande dentro del rango",
      concept:"MAX() es la forma más rápida de encontrar el mejor resultado sin revisar celda por celda.",
      xp:15, check: c => { const v=c["B10"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=max(b4:b8)"; } },
    { id:"e2", phase:1, phaseLabel:"Máximo y mínimo",
      label:"Calificación más baja",
      description:"En B11 encuentra la calificación más baja: =MIN(B4:B8)",
      hint:"MIN(rango) devuelve el valor más pequeño",
      concept:"MIN() y MAX() juntos te muestran el rango completo de resultados de un grupo.",
      xp:15, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=min(b4:b8)"; } },
    { id:"e3", phase:2, phaseLabel:"Promedio y conteo",
      label:"Promedio del grupo",
      description:"En B12 calcula el promedio de todas las calificaciones: =PROMEDIO(B4:B8)",
      hint:"PROMEDIO(rango) suma todos los valores y los divide entre la cantidad de celdas",
      concept:"El promedio te dice el nivel general del grupo, más allá de un solo alumno.",
      xp:20, check: c => { const v=c["B12"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=promedio(b4:b8)"||v==="=average(b4:b8)"; } },
    { id:"e4", phase:2, phaseLabel:"Promedio y conteo",
      label:"Cuántos alumnos fueron evaluados",
      description:"En B13 cuenta cuántas calificaciones hay registradas: =CONTAR(B4:B8)",
      hint:"CONTAR(rango) cuenta solo las celdas que contienen números",
      concept:"CONTAR() es útil para saber el tamaño de tu muestra de datos antes de sacar conclusiones.",
      xp:15, check: c => { const v=c["B13"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=contar(b4:b8)"||v==="=count(b4:b8)"; } },
  ],
};


// EX2103 — Fechas Simples: resta de fechas representadas como número de día
export const EJERCICIO_FECHASSIMPLES = {
  title: "Fechas Simples en Excel",
  subtitle: "Nivel Básico · Funciones y fórmulas",
  badge: "📆",
  description: "Aprende a restar fechas (representadas como números de día) para calcular cuántos días han pasado entre dos eventos.",
  completionText: "¡Ya sabes calcular la diferencia entre dos fechas en Excel!",
  introTitle: "Las fechas también se restan",

  cols: ["A","B","C","D"],
  rows: 9,
  colWidths: { A:120, B:130, C:130, D:150 },

  lockedCells: ["A1","A3","B3","C3","D3","A4","A5","A6","B4","B5","B6","C4","C5","C6","A8"],

  introFunctions: [
    ["=C4-B4", "Resta dos números de día para obtener los días transcurridos"],
    ["=PROMEDIO(rango)", "Calcula el promedio de una lista de resultados"],
  ],

  initialData: {
    A1:"REGISTRO DE ENTREGAS (días del año)",
    A3:"Pedido", B3:"Día de compra", C3:"Día de entrega", D3:"Días para entregar",
    A4:"Pedido 1", B4:"10", C4:"15",
    A5:"Pedido 2", B5:"20", C5:"33",
    A6:"Pedido 3", B6:"5",  C6:"9",
    A8:"Promedio de días de entrega →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:12 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if (id==="D4"||id==="D5"||id==="D6"||id==="D8") {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Días para Entregar por Pedido",
    emptyMsg: "Completa D4:D6 para ver la gráfica",
    bars: [
      { label:"Pedido 1", cellId:"D4", color:"#3b82f6" },
      { label:"Pedido 2", cellId:"D5", color:"#f59e0b" },
      { label:"Pedido 3", cellId:"D6", color:"#8b5cf6" },
    ],
    format: (v) => `${v} días`,
  },

  objectives: [
    { id:"f1", phase:1, phaseLabel:"Restar fechas",
      label:"Días que tardó el Pedido 1",
      description:"En D4 resta el día de compra al día de entrega: =C4-B4",
      hint:"C4-B4 te da cuántos días pasaron entre la compra y la entrega",
      concept:"En Excel, restar dos fechas (o números que representan fechas) te da directamente la diferencia en días.",
      xp:20, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=c4-b4"; } },
    { id:"f2", phase:1, phaseLabel:"Restar fechas",
      label:"Días de los demás pedidos",
      description:"Completa D5 y D6 con la misma resta",
      hint:"=C5-B5, =C6-B6",
      concept:"Replicar la resta te permite medir el tiempo de entrega de todos tus pedidos a la vez.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=c${r}-b${r}`; };
        return ok("D5",5)&&ok("D6",6);
      } },
    { id:"f3", phase:2, phaseLabel:"Resumen",
      label:"Promedio de días de entrega",
      description:"En D8 calcula el promedio de los días de entrega de los 3 pedidos: =PROMEDIO(D4:D6)",
      hint:"PROMEDIO(D4:D6) — D4, D5 y D6 ya tienen los días calculados",
      concept:"Con el promedio puedes saber si, en general, tus entregas están tardando más o menos de lo esperado.",
      xp:20, check: c => { const v=c["D8"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=promedio(d4:d6)"||v==="=average(d4:d6)"; } },
  ],
};


// EX2102 — Porcentajes y Descuentos
export const EJERCICIO_PORCENTAJES = {
  title: "Porcentajes y Descuentos",
  subtitle: "Nivel Básico · Funciones y fórmulas",
  badge: "🏷️",
  description: "Aprende a calcular descuentos y precios finales usando porcentajes — una de las operaciones más usadas en Excel para negocios.",
  completionText: "¡Ya sabes calcular descuentos y precios finales con porcentajes!",
  introTitle: "El signo % en tus fórmulas",

  cols: ["A","B","C","D","E"],
  rows: 8,
  colWidths: { A:110, B:100, C:110, D:140, E:120 },

  lockedCells: ["A1","A3","B3","C3","D3","E3","A4","A5","A6","B4","B5","B6","C4","C5","C6"],

  introFunctions: [
    ["=B4*C4", "Multiplica el precio por el porcentaje de descuento (en decimal)"],
    ["=B4-D4", "Resta el descuento calculado al precio original"],
  ],

  initialData: {
    A1:"OFERTAS DE TEMPORADA",
    A3:"Producto", B3:"Precio", C3:"% Descuento", D3:"Monto Descuento", E3:"Precio Final",
    A4:"Camisa",   B4:"450",  C4:"0.20",
    A5:"Pantalón", B5:"680",  C5:"0.15",
    A6:"Zapatos",  B6:"1200", C6:"0.30",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1") return { bg:"#7c2d12", color:"white", fontWeight:800, fontSize:12 };
    if (row===3)   return { bg:"#fde68a", color:"#7c2d12", fontWeight:700, fontSize:12 };
    if ((col==="D"||col==="E") && row>=4 && row<=6) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#fff7ed":"#f8fafc", color:v?.startsWith("=")?"#9a3412":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Precio Final por Producto",
    emptyMsg: "Completa la columna E para ver la gráfica",
    bars: [
      { label:"Camisa",   cellId:"E4", color:"#f59e0b" },
      { label:"Pantalón", cellId:"E5", color:"#3b82f6" },
      { label:"Zapatos",  cellId:"E6", color:"#8b5cf6" },
    ],
    format: (v) => `${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"p1", phase:1, phaseLabel:"Calcular el descuento",
      label:"Monto de descuento de la Camisa",
      description:"En D4 multiplica el precio por el porcentaje de descuento: =B4*C4",
      hint:"El porcentaje ya está en decimal (0.20 = 20%), solo multiplica",
      concept:"En Excel, un porcentaje se guarda como decimal (20% = 0.20). Multiplicar por ese decimal calcula directamente la parte proporcional.",
      xp:20, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b4*c4"; } },
    { id:"p2", phase:1, phaseLabel:"Calcular el descuento",
      label:"Monto de descuento de los demás productos",
      description:"Completa D5 y D6 con la misma fórmula",
      hint:"=B5*C5, =B6*C6",
      concept:"El mismo patrón sirve para cualquier lista de precios con descuento variable.",
      xp:20, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=b${r}*c${r}`; };
        return ok("D5",5)&&ok("D6",6);
      } },
    { id:"p3", phase:2, phaseLabel:"Calcular el precio final",
      label:"Precio final de la Camisa",
      description:"En E4 resta el descuento al precio original: =B4-D4",
      hint:"D4 ya tiene el monto de descuento calculado",
      concept:"Separar 'calcular el descuento' de 'restarlo al precio' hace la hoja más clara y fácil de auditar.",
      xp:20, check: c => { const v=c["E4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=b4-d4"; } },
    { id:"p4", phase:2, phaseLabel:"Calcular el precio final",
      label:"Precio final de los demás productos",
      description:"Completa E5 y E6",
      hint:"=B5-D5, =B6-D6",
      concept:"Con este patrón puedes calcular ofertas para cualquier catálogo de productos.",
      xp:20, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=b${r}-d${r}`; };
        return ok("E5",5)&&ok("E6",6);
      } },
  ],
};


// ───────────────────────────── NIVEL INTERMEDIO (continuación) ─────────────────────────────

// EI2302 — ÍNDICE y COINCIDIR
export const EJERCICIO_INDICECOINCIDIR = {
  title: "Búsqueda con ÍNDICE y COINCIDIR",
  subtitle: "Nivel Intermedio · Funciones",
  badge: "🧭",
  description: "Aprende a combinar ÍNDICE y COINCIDIR para buscar datos de forma más flexible que BUSCARV, sin depender del orden de las columnas.",
  completionText: "¡Ya sabes combinar ÍNDICE y COINCIDIR, una alternativa más flexible a BUSCARV!",
  introTitle: "Busca sin depender del orden de columnas",

  cols: ["A","B","C"],
  rows: 13,
  colWidths: { A:150, B:120, C:110 },

  lockedCells: ["A1","A3","B3","A4","A5","A6","A7","B4","B5","B6","B7","A9","A10","A11","A12","A13"],

  introFunctions: [
    ["=COINCIDIR(valor,rango,0)", "Devuelve la posición de un valor dentro de un rango"],
    ["=INDICE(rango,posición)", "Devuelve el valor que está en esa posición dentro de otro rango"],
    ["=INDICE(rango_resultado,COINCIDIR(valor,rango_busqueda,0))", "Combina ambas para buscar en cualquier dirección"],
  ],

  initialData: {
    A1:"VENTAS POR SUCURSAL",
    A3:"Sucursal", B3:"Ventas",
    A4:"Norte",  B4:"85000",
    A5:"Sur",    B5:"62000",
    A6:"Centro", B6:"91000",
    A7:"Este",   B7:"73000",
    A9:"CONSULTA",
    A10:"Sucursal a buscar →", B10:"Centro",
    A11:"Posición en la lista →",
    A12:"Ventas encontradas (paso a paso) →",
    A13:"Ventas encontradas (todo en un paso) →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#1d6f42", color:"white", fontWeight:800, fontSize:13 };
    if (row===3)   return { bg:"#d1fae5", color:"#064e3b", fontWeight:700, fontSize:12 };
    if (id==="A9") return { bg:"#1d6f42", color:"white", fontWeight:700, fontSize:11 };
    if (id==="B10") return { bg:"#fefce8", color:"#92400e", fontWeight:700 };
    if (["B11","B12","B13"].includes(id)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f0fdf4":"#f8fafc", color:v?.startsWith("=")?"#166534":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Ventas por Sucursal",
    emptyMsg: "Ya hay ventas registradas por sucursal",
    bars: [
      { label:"Norte",  cellId:"B4", color:"#1d6f42" },
      { label:"Sur",    cellId:"B5", color:"#3b82f6" },
      { label:"Centro", cellId:"B6", color:"#f59e0b" },
      { label:"Este",   cellId:"B7", color:"#ef4444" },
    ],
    format: (v) => `${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"ic1", phase:1, phaseLabel:"Encontrar la posición",
      label:"¿En qué posición está Centro?",
      description:"En B11 usa COINCIDIR para saber en qué fila de la lista está la sucursal de B10: =COINCIDIR(B10,A4:A7,0)",
      hint:"COINCIDIR(B10,A4:A7,0) busca B10 dentro de A4:A7 y regresa su posición (1, 2, 3...)",
      concept:"COINCIDIR no regresa un dato relacionado como BUSCARV — regresa la POSICIÓN donde encontró el valor dentro del rango.",
      xp:25, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=coincidir(b10,a4:a7,0)"||v==="=match(b10,a4:a7,0)"; } },
    { id:"ic2", phase:2, phaseLabel:"Traer el dato con esa posición",
      label:"Ventas de esa sucursal, paso a paso",
      description:"En B12 usa ÍNDICE con la posición ya calculada en B11: =INDICE(B4:B7,B11)",
      hint:"ÍNDICE(rango,posición) regresa el valor que está en esa posición del rango B4:B7",
      concept:"ÍNDICE(rango,posición) es como decir 'dame el elemento número X de esta lista'. Combinado con COINCIDIR, puedes buscar el dato sin que la columna de resultado esté a la derecha, como exige BUSCARV.",
      xp:25, check: c => { const v=c["B12"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=indice(b4:b7,b11)"||v==="=index(b4:b7,b11)"; } },
    { id:"ic3", phase:2, phaseLabel:"Traer el dato con esa posición",
      label:"Todo en una sola fórmula",
      description:"En B13 combina COINCIDIR dentro de ÍNDICE en un solo paso: =INDICE(B4:B7,COINCIDIR(B10,A4:A7,0))",
      hint:"Sustituye COINCIDIR(...) directamente donde antes escribiste el número de posición",
      concept:"Anidar COINCIDIR dentro de ÍNDICE evita depender de una celda intermedia — es la forma más común de usar esta combinación en el trabajo real.",
      xp:30, check: c => { const v=c["B13"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=indice(b4:b7,coincidir(b10,a4:a7,0))"||v==="=index(b4:b7,match(b10,a4:a7,0))"; } },
    { id:"ic4", phase:2, phaseLabel:"Traer el dato con esa posición",
      label:"Prueba con otra sucursal",
      description:'Cambia B10 a "Este" y confirma que B11, B12 y B13 se actualizan automáticamente',
      hint:"Solo edita B10 — no toques las fórmulas de B11, B12 ni B13",
      concept:"Esta es la ventaja real: una sola fórmula sirve para consultar cualquier sucursal de la lista.",
      xp:20, check: c => c["B10"]?.trim().toLowerCase()==="este" },
  ],
};


// EI2302 — Funciones de Texto Avanzadas: LARGO, EXTRAE, HALLAR, SUSTITUIR
export const EJERCICIO_TEXTOAVANZADO = {
  title: "Funciones de Texto Avanzadas",
  subtitle: "Nivel Intermedio · Funciones",
  badge: "🔡",
  description: "Aprende a medir, extraer, buscar y reemplazar texto con LARGO, EXTRAE, HALLAR y SUSTITUIR — funciones clave para limpiar y transformar datos.",
  completionText: "¡Ya dominas las funciones de texto más usadas para limpiar y transformar datos!",
  introTitle: "Limpia y transforma texto como un experto",

  cols: ["A","B","C","D"],
  rows: 13,
  colWidths: { A:190, B:90, C:90, D:120 },

  lockedCells: ["A1","A3","B3","C3","A4","A5","A6","A9","A10","B10","D10","A11","A12"],

  introFunctions: [
    ["=LARGO(texto)", "Cuenta cuántos caracteres tiene un texto"],
    ["=EXTRAE(texto,inicio,núm_caracteres)", "Extrae una parte del texto desde una posición específica"],
    ["=HALLAR(texto_buscado,dentro_del_texto)", "Encuentra en qué posición aparece un texto dentro de otro"],
    ['=SUSTITUIR(texto,"viejo","nuevo")', "Reemplaza todas las apariciones de un texto por otro"],
  ],

  initialData: {
    A1:"DIRECTORIO DE CORREOS Y TELÉFONOS",
    A3:"Correo", B3:"Longitud", C3:"Posición @",
    A4:"ana.garcia@empresa.com",
    A5:"luis.perez@empresa.com",
    A6:"maria.lopez@empresa.com",
    A9:"LIMPIEZA DE TELÉFONOS",
    A10:"Teléfono", B10:"Sin guiones", D10:"Código de área",
    A11:"555-123-4567",
    A12:"555-987-6543",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1"||id==="A9") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:12 };
    if (row===3||row===10)    return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:12 };
    if ((col==="B"||col==="C") && row>=4 && row<=6) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    if ((col==="B"||col==="D") && row>=11 && row<=12) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"tx1", phase:1, phaseLabel:"Medir texto",
      label:"Longitud del primer correo",
      description:"En B4 cuenta cuántos caracteres tiene el correo de A4: =LARGO(A4)",
      hint:"LARGO(texto) cuenta todos los caracteres, incluyendo el punto y la arroba",
      concept:"LARGO() es útil para validar formatos: por ejemplo, verificar que un código o correo tenga la longitud esperada.",
      xp:15, check: c => { const v=c["B4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=largo(a4)"||v==="=len(a4)"; } },
    { id:"tx2", phase:1, phaseLabel:"Medir texto",
      label:"Longitud de los demás correos",
      description:"Completa B5 y B6 con la misma fórmula",
      hint:"=LARGO(A5), =LARGO(A6)",
      concept:"Replicar LARGO() te permite validar el formato de una lista completa de datos de una sola vez.",
      xp:20, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=largo(a${r})`||v===`=len(a${r})`; };
        return ok("B5",5)&&ok("B6",6);
      } },
    { id:"tx3", phase:2, phaseLabel:"Buscar dentro de texto",
      label:"Posición del @ en el primer correo",
      description:'En C4 encuentra en qué posición está el símbolo @ dentro del correo: =HALLAR("@",A4)',
      hint:'HALLAR("@",A4) busca el símbolo @ dentro del texto de A4 y regresa su posición',
      concept:"HALLAR() te dice EN QUÉ POSICIÓN está un carácter o palabra dentro de un texto — muy útil antes de usar EXTRAE().",
      xp:25, check: c => { const v=c["C4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=hallar("@",a4)'||v==='=search("@",a4)'; } },
    { id:"tx4", phase:3, phaseLabel:"Extraer texto",
      label:"Código de área de los teléfonos",
      description:"En D11 y D12 extrae los primeros 3 caracteres de cada teléfono (código de área): =EXTRAE(A11,1,3)",
      hint:"EXTRAE(texto,inicio,cantidad) — empieza en la posición 1 y toma 3 caracteres",
      concept:"EXTRAE() te permite tomar cualquier parte de un texto, no solo el inicio o el final como IZQUIERDA/DERECHA.",
      xp:30, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=extrae(a${r},1,3)`||v===`=mid(a${r},1,3)`; };
        return ok("D11",11)&&ok("D12",12);
      } },
    { id:"tx5", phase:4, phaseLabel:"Reemplazar texto",
      label:"Quita los guiones de los teléfonos",
      description:'En B11 y B12 reemplaza todos los guiones por nada: =SUSTITUIR(A11,"-","")',
      hint:'SUSTITUIR(texto,"-","") busca cada guion y lo reemplaza por texto vacío, es decir, lo elimina',
      concept:"SUSTITUIR() es la forma de 'buscar y reemplazar' dentro de una fórmula — así limpian los teléfonos los sistemas reales antes de guardarlos.",
      xp:30, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=sustituir(a${r},"-","")`; };
        return ok("B11",11)&&ok("B12",12);
      } },
  ],
};


// EI2302 — Lógica Combinada: SI + Y() + O()
export const EJERCICIO_SIYO = {
  title: "Lógica Combinada: SI, Y y O",
  subtitle: "Nivel Intermedio · Funciones",
  badge: "🚦",
  description: "Aprende a combinar Y() y O() dentro de SI() para evaluar reglas de negocio con múltiples condiciones a la vez.",
  completionText: "¡Ya sabes combinar SI, Y y O para construir reglas de negocio con varias condiciones!",
  introTitle: "Cuando necesitas Y además de O",

  cols: ["A","B","C","D"],
  rows: 11,
  colWidths: { A:100, B:130, C:130, D:130 },

  lockedCells: ["A1","A3","B3","C3","D3","A4","A5","A6","B4","B5","B6","C4","C5","C6","A8","A9","B9","C9","D9","A10","B10","C10","A11","B11","C11"],

  introFunctions: [
    ["=Y(cond1,cond2)", "Verdadero solo si TODAS las condiciones se cumplen"],
    ["=O(cond1,cond2)", "Verdadero si AL MENOS UNA condición se cumple"],
    ['=SI(Y(A1>=6,B1>=6),"Aprobado","Reprobado")', "Aprueba solo si ambas materias tienen 6 o más"],
  ],

  initialData: {
    A1:"APROBACIÓN DE CURSO (requiere ambas materias)",
    A3:"Alumno", B3:"Matemáticas", C3:"Español", D3:"Resultado",
    A4:"Ana",  B4:"8", C4:"7",
    A5:"Luis", B5:"5", C5:"9",
    A6:"María",B6:"6", C6:"6",
    A8:"ELEGIBILIDAD PARA DESCUENTO (requiere al menos un requisito)",
    A9:"Alumno", B9:"Alumno nuevo", C9:"Recomendado", D9:"Elegible",
    A10:"Ana",  B10:"No", C10:"Sí",
    A11:"Luis", B11:"No", C11:"No",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1") return { bg:"#1e3a5f", color:"white", fontWeight:800, fontSize:11 };
    if (id==="A8") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:11 };
    if (row===3)   return { bg:"#dbeafe", color:"#1e40af", fontWeight:700, fontSize:11 };
    if (row===9)   return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:11 };
    if (id[0]==="D" && (row===4||row===5||row===6)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#eff6ff":"#f8fafc", color:v?.startsWith("=")?"#1e40af":"#94a3b8", fontWeight:600 };
    }
    if (id[0]==="D" && (row===10||row===11)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"yo1", phase:1, phaseLabel:"Y() — todas las condiciones",
      label:"¿Ana aprueba el curso?",
      description:'En D4 usa Y() para aprobar solo si AMBAS materias son mayores o iguales a 6: =SI(Y(B4>=6,C4>=6),"Aprobado","Reprobado")',
      hint:"Y(B4>=6,C4>=6) es verdadero solo si las DOS condiciones se cumplen al mismo tiempo",
      concept:"Y() exige que TODAS las condiciones sean verdaderas. Si alguien reprueba una materia, Y() será falso aunque apruebe la otra.",
      xp:25, check: c => { const v=c["D4"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(y(b4>=6,c4>=6),"aprobado","reprobado")'||v==='=if(and(b4>=6,c4>=6),"aprobado","reprobado")'; } },
    { id:"yo2", phase:1, phaseLabel:"Y() — todas las condiciones",
      label:"Resultado de los demás alumnos",
      description:"Completa D5 y D6 con la misma fórmula",
      hint:"Misma fórmula, cambia solo la fila (B5,C5) y (B6,C6)",
      concept:"Con Y(), Luis reprueba a pesar de tener buena nota en Español, porque no cumplió AMBAS condiciones.",
      xp:25, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(y(b${r}>=6,c${r}>=6),"aprobado","reprobado")`||v===`=if(and(b${r}>=6,c${r}>=6),"aprobado","reprobado")`; };
        return ok("D5",5)&&ok("D6",6);
      } },
    { id:"yo3", phase:2, phaseLabel:"O() — al menos una condición",
      label:"¿Ana es elegible para el descuento?",
      description:'En D10 usa O() para dar el descuento si CUALQUIERA de los dos requisitos se cumple: =SI(O(B10="Sí",C10="Sí"),"Elegible","No elegible")',
      hint:'O(B10="Sí",C10="Sí") es verdadero si AL MENOS UNO de los dos dice "Sí"',
      concept:"O() es lo opuesto a Y(): basta con que UNA condición se cumpla para que el resultado sea verdadero.",
      xp:30, check: c => { const v=c["D10"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(o(b10="sí",c10="sí"),"elegible","noelegible")'||v==='=if(or(b10="sí",c10="sí"),"elegible","noelegible")'; } },
    { id:"yo4", phase:2, phaseLabel:"O() — al menos una condición",
      label:"Elegibilidad de Luis",
      description:"Completa D11 con la misma fórmula",
      hint:"Misma fórmula, cambia solo la fila (B11,C11)",
      concept:"Luis no es alumno nuevo NI fue recomendado, así que ninguna condición de O() se cumple: no es elegible.",
      xp:25, check: c => { const v=c["D11"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(o(b11="sí",c11="sí"),"elegible","noelegible")'||v==='=if(or(b11="sí",c11="sí"),"elegible","noelegible")'; } },
  ],
};


// ───────────────────────────── NIVEL APLICADO (continuación) ─────────────────────────────

// EA1612 — Nómina: Bonos por desempeño y resumen por departamento
export const EJERCICIO_NOMINA = {
  title: "Nómina: Bonos y Deducciones",
  subtitle: "Nivel Aplicado · Gestión Humana",
  badge: "💵",
  description: "Calcula bonos de productividad y agrupa el total de nómina por departamento — funciones clave para cualquier área de Recursos Humanos.",
  completionText: "¡Puedes calcular bonos automáticos y agrupar nómina por departamento, tal como en un sistema de RH real!",
  introTitle: "La nómina también se automatiza",

  cols: ["A","B","C","D","E"],
  rows: 11,
  colWidths: { A:110, B:110, C:110, D:120, E:100 },

  lockedCells: ["A1","A3","B3","C3","D3","E3","A4","A5","A6","A7","B4","B5","B6","B7","C4","C5","C6","C7","D4","D5","D6","D7","A9","A10","A11"],

  introFunctions: [
    ['=SI(B4>=90,C4*0.10,0)', "Bono del 10% si el desempeño es 90 o más, si no, sin bono"],
    ["=SUMAR.SI(rango_depto,criterio,rango_sueldo)", "Suma los sueldos de un departamento específico"],
  ],

  initialData: {
    A1:"NÓMINA MENSUAL",
    A3:"Empleado", B3:"Desempeño", C3:"Sueldo Base", D3:"Departamento", E3:"Bono",
    A4:"Carlos", B4:"95", C4:"12000", D4:"Ventas",
    A5:"Diana",  B5:"80", C5:"15000", D5:"Ventas",
    A6:"Miguel", B6:"92", C6:"10000", D6:"Sistemas",
    A7:"Elena",  B7:"70", C7:"11000", D7:"Sistemas",
    A9:"RESUMEN POR DEPARTAMENTO",
    A10:"Total sueldos Ventas →",
    A11:"Total sueldos Sistemas →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1"||id==="A9") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:12 };
    if (row===3) return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if (id[0]==="E" && row>=4 && row<=7) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:600 };
    }
    if (id==="B10"||id==="B11") {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Sueldo Base por Empleado",
    emptyMsg: "Ya hay sueldos registrados",
    bars: [
      { label:"Carlos", cellId:"C4", color:"#4c1d95" },
      { label:"Diana",  cellId:"C5", color:"#3b82f6" },
      { label:"Miguel", cellId:"C6", color:"#f59e0b" },
      { label:"Elena",  cellId:"C7", color:"#ef4444" },
    ],
    format: (v) => `${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"n1", phase:1, phaseLabel:"Bono por desempeño",
      label:"Bono de Carlos",
      description:"En E4 calcula un bono del 10% del sueldo SOLO si el desempeño es 90 o más, si no, el bono es 0: =SI(B4>=90,C4*0.10,0)",
      hint:"SI(B4>=90, C4*0.10, 0) — el bono se calcula solo cuando la condición es verdadera",
      concept:"Puedes usar una operación matemática (C4*0.10) directamente como resultado de un SI(), no solo texto.",
      xp:25, check: c => { const v=c["E4"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=si(b4>=90,c4*0.10,0)"||v==="=if(b4>=90,c4*0.10,0)"; } },
    { id:"n2", phase:1, phaseLabel:"Bono por desempeño",
      label:"Bono de los demás empleados",
      description:"Completa E5, E6 y E7 con la misma fórmula",
      hint:"Misma fórmula, cambia solo la fila de desempeño y sueldo de cada uno",
      concept:"Con esta fórmula, el sistema calcula automáticamente qué empleados merecen bono cada mes, sin revisar manualmente.",
      xp:30, check: c => {
        const ok=(id,r)=>{ const v=c[id]?.trim().toLowerCase().replace(/\s/g,""); return v===`=si(b${r}>=90,c${r}*0.10,0)`||v===`=if(b${r}>=90,c${r}*0.10,0)`; };
        return ok("E5",5)&&ok("E6",6)&&ok("E7",7);
      } },
    { id:"n3", phase:2, phaseLabel:"Resumen por departamento",
      label:"Total de sueldos en Ventas",
      description:'En B10 suma los sueldos del departamento de Ventas: =SUMAR.SI(D4:D7,"Ventas",C4:C7)',
      hint:'SUMAR.SI(D4:D7,"Ventas",C4:C7) busca "Ventas" en la columna Departamento y suma el sueldo correspondiente',
      concept:"SUMAR.SI agrupa gastos de nómina por departamento sin necesidad de una tabla dinámica.",
      xp:30, check: c => { const v=c["B10"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=sumar.si(d4:d7,"ventas",c4:c7)'||v==='=sumif(d4:d7,"ventas",c4:c7)'; } },
    { id:"n4", phase:2, phaseLabel:"Resumen por departamento",
      label:"Total de sueldos en Sistemas",
      description:"En B11 haz lo mismo para Sistemas",
      hint:'=SUMAR.SI(D4:D7,"Sistemas",C4:C7)',
      concept:"La misma fórmula sirve para cualquier departamento, solo cambia el criterio de búsqueda.",
      xp:25, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=sumar.si(d4:d7,"sistemas",c4:c7)'||v==='=sumif(d4:d7,"sistemas",c4:c7)'; } },
  ],
};


// EA1611 — CRM: Búsqueda y clasificación de leads
export const EJERCICIO_CRM = {
  title: "CRM: Clasifica tus Leads",
  subtitle: "Nivel Aplicado · CRM",
  badge: "📇",
  description: "Busca el interés de cada prospecto en tu base de datos y clasifica automáticamente la prioridad de seguimiento — la base de cualquier sistema CRM.",
  completionText: "¡Puedes clasificar leads automáticamente según su interés, tal como un CRM profesional!",
  introTitle: "Prioriza a tus prospectos automáticamente",

  cols: ["A","B","C"],
  rows: 12,
  colWidths: { A:130, B:130, C:110 },

  lockedCells: ["A1","A3","B3","A4","A5","A6","A7","B4","B5","B6","B7","A9","A10","A11","A12"],

  introFunctions: [
    ["=BUSCARV(valor,tabla,columna,FALSO)", "Trae el nivel de interés registrado de cada prospecto"],
    ['=SI(B10="Alto","Contactar hoy","Dar seguimiento")', "Define la prioridad según el interés encontrado"],
  ],

  initialData: {
    A1:"BASE DE PROSPECTOS",
    A3:"Prospecto", B3:"Interés",
    A4:"Roberto", B4:"Alto",
    A5:"Carmen",  B5:"Bajo",
    A6:"Miguel",  B6:"Medio",
    A7:"Daniela", B7:"Alto",
    A9:"SEGUIMIENTO",
    A10:"Prospecto →", B10:"Miguel",
    A11:"Interés encontrado →",
    A12:"Prioridad →",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1));
    if (id==="A1"||id==="A9") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:12 };
    if (row===3) return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:12 };
    if (id==="B10") return { bg:"#fefce8", color:"#92400e", fontWeight:700 };
    if (id==="B11"||id==="B12") {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:700 };
    }
    return null;
  },

  chart: { enabled: false },

  objectives: [
    { id:"crm1", phase:1, phaseLabel:"Buscar el interés",
      label:"Interés de Miguel",
      description:"En B11 busca el interés registrado del prospecto en B10: =BUSCARV(B10,A4:B7,2,FALSO)",
      hint:"BUSCARV(B10,A4:B7,2,FALSO) busca el nombre en la columna A y regresa el interés (columna 2)",
      concept:"Antes de clasificar, primero necesitas TRAER el dato — para eso sirve BUSCARV.",
      xp:25, check: c => { const v=c["B11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=buscarv(b10,a4:b7,2,falso)"||v==="=vlookup(b10,a4:b7,2,false)"; } },
    { id:"crm2", phase:2, phaseLabel:"Clasificar la prioridad",
      label:"Prioridad de seguimiento",
      description:'En B12 usa SI para definir la prioridad: si el interés es "Alto", escribe "Contactar hoy", si no "Dar seguimiento": =SI(B11="Alto","Contactar hoy","Dar seguimiento")',
      hint:"B11 ya tiene el interés encontrado con BUSCARV",
      concept:"Separar 'buscar el dato' (BUSCARV) de 'decidir qué hacer con él' (SI) es el patrón que usan los sistemas CRM reales para priorizar el trabajo del equipo de ventas.",
      xp:30, check: c => { const v=c["B12"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(b11="alto","contactarhoy","darseguimiento")'||v==='=if(b11="alto","contactarhoy","darseguimiento")'; } },
    { id:"crm3", phase:2, phaseLabel:"Clasificar la prioridad",
      label:"Prueba con otro prospecto",
      description:'Cambia B10 a "Roberto" y confirma que B11 y B12 se actualizan automáticamente',
      hint:"Solo edita B10 — no toques las fórmulas de B11 ni B12",
      concept:"Con estas dos fórmulas, cualquier vendedor puede saber en segundos a quién contactar primero.",
      xp:20, check: c => c["B10"]?.trim().toLowerCase()==="roberto" },
  ],
};


// EA1613 — Comisiones de Venta: combina SUMAR.SI + multiplicación + SI
export const EJERCICIO_COMISIONES = {
  title: "Comisiones de Venta: Integra Varias Funciones",
  subtitle: "Nivel Aplicado · Comisiones",
  badge: "🏆",
  description: "Combina SUMAR.SI y SI en un solo reporte para calcular automáticamente comisiones de venta y verificar el cumplimiento de metas.",
  completionText: "¡Integraste SUMAR.SI, una multiplicación y SI en un solo reporte, igual que un analista de ventas experimentado!",
  introTitle: "Un reporte, varias funciones",

  cols: ["A","B","C","D","E","F"],
  rows: 12,
  colWidths: { A:110, B:100, C:100, D:120, E:110, F:110 },

  lockedCells: ["A1","A3","B3","A4","A5","A6","A7","B4","B5","B6","B7","A9","A10","B10","C10","D10","E10","F10","A11","B11","C11","A12","B12","C12"],

  introFunctions: [
    ["=SUMAR.SI(rango,criterio,rango_suma)", "Suma las ventas totales de un vendedor"],
    ["=D11*B11", "Multiplica el total vendido por su porcentaje de comisión"],
    ['=SI(D11>=C11,"Cumplida","No cumplida")', "Indica si el vendedor llegó a su meta"],
  ],

  initialData: {
    A1:"VENTAS DEL MES",
    A3:"Vendedor", B3:"Venta",
    A4:"Roberto", B4:"25000",
    A5:"Carmen",  B5:"18000",
    A6:"Roberto", B6:"12000",
    A7:"Carmen",  B7:"22000",
    A9:"TABLA DE COMISIONES Y REPORTE",
    A10:"Vendedor", B10:"% Comisión", C10:"Meta", D10:"Total Vendido", E10:"Comisión", F10:"Meta",
    A11:"Roberto", B11:"0.05", C11:"30000",
    A12:"Carmen",  B12:"0.04", C12:"35000",
  },

  getCellStyle: (id, cells) => {
    const row = parseInt(id.slice(1)); const col = id[0];
    if (id==="A1"||id==="A9") return { bg:"#4c1d95", color:"white", fontWeight:800, fontSize:12 };
    if (row===3||row===10) return { bg:"#ede9fe", color:"#4c1d95", fontWeight:700, fontSize:11 };
    if ((col==="D"||col==="E"||col==="F") && (row===11||row===12)) {
      const v = cells[id]?.trim();
      return { bg: v?.startsWith("=") ? "#f5f3ff":"#f8fafc", color:v?.startsWith("=")?"#5b21b6":"#94a3b8", fontWeight:600 };
    }
    return null;
  },

  chart: {
    enabled: true,
    title: "Total Vendido por Vendedor",
    emptyMsg: "Completa D11 y D12 para ver la gráfica",
    bars: [
      { label:"Roberto", cellId:"D11", color:"#4c1d95" },
      { label:"Carmen",  cellId:"D12", color:"#3b82f6" },
    ],
    format: (v) => `${Number(v).toLocaleString("es-MX")}`,
  },

  objectives: [
    { id:"cv1", phase:1, phaseLabel:"Sumar ventas totales",
      label:"Total vendido por Roberto",
      description:'En D11 suma todas las ventas de Roberto usando SUMAR.SI: =SUMAR.SI(A4:A7,"Roberto",B4:B7)',
      hint:'SUMAR.SI(A4:A7,"Roberto",B4:B7) suma todas las ventas donde el vendedor sea "Roberto"',
      concept:"Un vendedor puede aparecer en varias filas de ventas — SUMAR.SI agrupa automáticamente su total.",
      xp:25, check: c => { const v=c["D11"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=sumar.si(a4:a7,"roberto",b4:b7)'||v==='=sumif(a4:a7,"roberto",b4:b7)'; } },
    { id:"cv2", phase:1, phaseLabel:"Sumar ventas totales",
      label:"Total vendido por Carmen",
      description:"En D12 haz lo mismo para Carmen",
      hint:'=SUMAR.SI(A4:A7,"Carmen",B4:B7)',
      concept:"La misma fórmula sirve para cualquier vendedor, solo cambia el criterio.",
      xp:20, check: c => { const v=c["D12"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=sumar.si(a4:a7,"carmen",b4:b7)'||v==='=sumif(a4:a7,"carmen",b4:b7)'; } },
    { id:"cv3", phase:2, phaseLabel:"Calcular la comisión",
      label:"Comisión de Roberto",
      description:"En E11 multiplica el total vendido por su porcentaje de comisión: =D11*B11",
      hint:"D11 ya tiene el total vendido, B11 tiene el porcentaje",
      concept:"Con el total ya agrupado por SUMAR.SI, calcular la comisión es una simple multiplicación.",
      xp:20, check: c => { const v=c["E11"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=d11*b11"; } },
    { id:"cv4", phase:2, phaseLabel:"Calcular la comisión",
      label:"Comisión de Carmen",
      description:"En E12 haz lo mismo",
      hint:"=D12*B12",
      concept:"El mismo patrón calcula la comisión de cualquier vendedor una vez que tienes su total vendido.",
      xp:20, check: c => { const v=c["E12"]?.trim().toLowerCase().replace(/\s/g,""); return v==="=d12*b12"; } },
    { id:"cv5", phase:3, phaseLabel:"Verificar la meta",
      label:"¿Roberto cumplió su meta?",
      description:'En F11 usa SI para saber si el total vendido llegó a la meta: =SI(D11>=C11,"Cumplida","No cumplida")',
      hint:"D11 ya tiene el total vendido, C11 tiene la meta",
      concept:"Combinar SUMAR.SI (agrupar), multiplicación (comisión) y SI (evaluar meta) es exactamente el tipo de reporte que arma un analista de ventas real.",
      xp:25, check: c => { const v=c["F11"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(d11>=c11,"cumplida","nocumplida")'||v==='=if(d11>=c11,"cumplida","nocumplida")'; } },
    { id:"cv6", phase:3, phaseLabel:"Verificar la meta",
      label:"¿Carmen cumplió su meta?",
      description:"En F12 haz lo mismo",
      hint:'=SI(D12>=C12,"Cumplida","No cumplida")',
      concept:"Con este reporte completo, cualquier gerente de ventas ve de un vistazo quién cumplió su meta y cuánto se le debe de comisión.",
      xp:25, check: c => { const v=c["F12"]?.trim().toLowerCase().replace(/\s/g,""); return v==='=si(d12>=c12,"cumplida","nocumplida")'||v==='=if(d12>=c12,"cumplida","nocumplida")'; } },
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
//  CATÁLOGO AGRUPADO — para alimentar un selector de ejercicios en la plataforma
// ═══════════════════════════════════════════════════════════════════════════════
export const EJERCICIOS_CATALOGO = [
  { key:"referencias",     label:"📌 Referencias Absolutas",   tag:"Básico",     config: EJERCICIO_REFERENCIAS },
  { key:"texto",           label:"🔤 Funciones de Texto",      tag:"Básico",     config: EJERCICIO_TEXTO },
  { key:"validacion",      label:"🔍 Duplicados con CONTAR.SI", tag:"Básico",    config: EJERCICIO_VALIDACION },
  { key:"estadisticas",    label:"📈 Funciones Estadísticas",  tag:"Básico",     config: EJERCICIO_ESTADISTICAS },
  { key:"fechassimples",   label:"📆 Fechas Simples",          tag:"Básico",     config: EJERCICIO_FECHASSIMPLES },
  { key:"porcentajes",     label:"🏷️ Porcentajes y Descuentos", tag:"Básico",    config: EJERCICIO_PORCENTAJES },
  { key:"buscarv",         label:"🔎 BUSCARV",                 tag:"Intermedio", config: EJERCICIO_BUSCARV },
  { key:"sumarsi",         label:"➕ SUMAR.SI y CONTAR.SI",     tag:"Intermedio", config: EJERCICIO_SUMARSI },
  { key:"fechas",          label:"📅 Cálculo de Vencimientos", tag:"Intermedio", config: EJERCICIO_FECHAS },
  { key:"indicecoincidir", label:"🧭 ÍNDICE y COINCIDIR",      tag:"Intermedio", config: EJERCICIO_INDICECOINCIDIR },
  { key:"textoavanzado",   label:"🔡 Funciones de Texto Avanzadas", tag:"Intermedio", config: EJERCICIO_TEXTOAVANZADO },
  { key:"siyo",            label:"🚦 Lógica Combinada: SI, Y y O", tag:"Intermedio", config: EJERCICIO_SIYO },
  { key:"facturacion",     label:"🧾 Facturación con BUSCARV", tag:"Aplicado",   config: EJERCICIO_FACTURACION },
  { key:"sierror",         label:"🛡️ Manejo de Errores (SI.ERROR)", tag:"Aplicado", config: EJERCICIO_SIERROR },
  { key:"cuentascobrar",   label:"💰 Cuentas por Cobrar",      tag:"Aplicado",   config: EJERCICIO_CUENTASCOBRAR },
  { key:"nomina",          label:"💵 Nómina: Bonos y Deducciones", tag:"Aplicado", config: EJERCICIO_NOMINA },
  { key:"crm",             label:"📇 CRM: Clasifica tus Leads", tag:"Aplicado",  config: EJERCICIO_CRM },
  { key:"comisiones",      label:"🏆 Comisiones de Venta",     tag:"Aplicado",   config: EJERCICIO_COMISIONES },
];

export default EJERCICIOS_CATALOGO;
